/**
 * Compute seconds until the next expected sync for a collector.
 *
 * Formula: `finishedAt + pollIntervalMs - now`, floored into seconds.
 * Returns `0` when the next sync is due now or overdue, which the UI
 * renders as "—" via `inFuture(0)`.
 *
 * `nowMs` is parameterised so mockup mode can pass `FROZEN_NOW_MS` while
 * live mode passes `Date.now()` — keeps the function pure and the
 * frozen-time ESLint rule happy (`new Date()` / `Date.now()` stay scoped
 * to fixture files and explicit callers).
 */
export function computeNextSyncInS(
  finishedAtIso: string | null,
  pollIntervalMs: number,
  nowMs: number,
): number {
  if (!finishedAtIso) return 0;
  const finishedMs = Date.parse(finishedAtIso);
  if (!Number.isFinite(finishedMs)) return 0;
  const diffS = Math.floor((finishedMs + pollIntervalMs - nowMs) / 1000);
  return diffS > 0 ? diffS : 0;
}

/**
 * Default frontend fallback for the worker poll cadence — matches the
 * backend `WORKER_POLL_INTERVAL_MS` tier-2 default (15 minutes). The
 * backend does not yet surface this value per-request, so the frontend
 * uses the shared default. Overriding per-collector via
 * `pollIntervalMsOverride` is the tier-3 seam that will light up once the
 * `/collectors` response starts returning it (see task 105b).
 */
export const DEFAULT_POLL_INTERVAL_MS = 15 * 60 * 1000;
